import type { TravelLeg } from "@housing/shared";

import type { TravelMode } from "@/travel/travel.model.ts";

/**
 * Every time on screen is prefixed "~". These are modelled minutes against a
 * typical weekday morning, not a promise about Tuesday — the tilde says so
 * without spending a word on "approx", and matches how the card already marks an
 * approximate post date.
 */
export function travelTime(mins: number): string {
  if (mins < 60) return `~${mins} min`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest === 0 ? `~${hours} hr` : `~${hours} hr ${rest} min`;
}

/**
 * "Combined" rather than "Transit" for the third row: what earns it a place is
 * that chaining several modes together beats doing one of them alone, which is
 * the thing worth telling the reader. (The internal key stays `transit` — it is
 * persisted in saved filter state.)
 */
export const MODE_LABELS: Record<TravelMode, string> = {
  walk: "Walk",
  scooter: "Scooter",
  transit: "Combined",
};

/** Source-reported leg modes are open-ended; anything unmapped prints as-is. */
const LEG_MODE_LABELS: Record<string, string> = {
  walk: "walk",
  bus: "bus",
  train: "train",
  rail_underground: "metro",
  rail: "rail",
  ferry: "ferry",
  cable_car: "cable car",
  coach: "coach",
};

function legModeLabel(mode: string): string {
  return LEG_MODE_LABELS[mode] ?? mode.replace(/_/g, " ");
}

/** "6m walk → 13m bus 38R → 4m walk" — the itinerary, once expanded. */
export function legsLabel(legs: TravelLeg[]): string {
  return legs
    .map((l) => `${l.mins}m ${legModeLabel(l.mode)}${l.line ? ` ${l.line}` : ""}`)
    .join(" → ");
}

/** "3 legs" — the collapsed summary. Only ever shown for a genuine chain. */
export function legsSummary(legs: TravelLeg[]): string {
  return `${legs.length} legs`;
}
